// LICENSE PROTECTED SOFTWARE
import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Lock, FileCheck, AlertTriangle, Mail } from 'lucide-react';

export default function PrivacyNoticePage() {
  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" /> Ana Sayfa
        </Link>
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-semibold text-slate-900">Gizlilik ve KVKK Aydınlatma Metni</h1>
        </div>
        <p className="text-sm text-slate-500 mb-8">Son güncelleme: {new Date().toLocaleDateString('tr-TR')}</p>

        <div className="space-y-6 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2"><FileCheck className="w-5 h-5"/> 1. Veri Sorumlusu</h2>
            <p>Bu platform, Kent Konseyi (bundan sonra "Veri Sorumlusu") tarafından işletilmektedir. 6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) kapsamında aşağıdaki bilgiler tarafınıza sunulmaktadır.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">2. İşlenen Kişisel Veriler</h2>
            <p>Platforma kayıt sırasında ve kullanım süresince aşağıdaki kişisel verileriniz işlenmektedir:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Kimlik:</strong> Ad, soyad, T.C. kimlik numarası, doğum tarihi, cinsiyet</li>
              <li><strong>İletişim:</strong> E-posta, telefon, açık adres (sokak, bina no, daire no dahil), il/ilçe/mahalle</li>
              <li><strong>Demografik:</strong> Eğitim durumu, meslek, mezun olunan/devam edilen okul adı (opsiyonel), üyesi/gönüllüsü olunan kuruluş (opsiyonel)</li>
              <li><strong>Üyelik:</strong> Rol (vatandaş, muhtar, STK temsilcisi), KVKK onay tarihi ve IP</li>
              <li><strong>Muhtar bilgileri (sadece muhtarlar için):</strong> Mahalle adı, aza sayısı, aza isimleri (varsa), çalışma saatleri, muhtarlık iletişim bilgileri</li>
              <li><strong>STK bilgileri (sadece STK temsilcileri için):</strong> Kurum adı, türü, sicil/DERBİS numarası, vergi numarası, kurum telefonu/e-postası/açık adresi/web sitesi, faaliyet alanı, üye sayısı, yönetim kurulu bilgileri</li>
              <li><strong>Yetkili ve Yedek Temsilci (STK):</strong> Ad, soyad, T.C. kimlik, görevi, telefon, e-posta</li>
              <li><strong>İçerik:</strong> Tarafınızca oluşturulan talepler, anket cevapları, mesajlar, görüşler</li>
              <li><strong>Belge:</strong> Başvurularınız ve sonradan talep edilen belgeler kapsamında yüklediğiniz dosyalar (PDF, görsel vb. — STK için yetki belgesi, tüzük vb.)</li>
              <li><strong>Teknik / Erişim Kayıtları:</strong> Oturum IP adresi, oturum açma/çıkış zamanı, başarısız giriş denemeleri, kullanıcı eylem geçmişi (audit log), tarayıcı türü; zorunlu güvenlik çerezleri</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2"><Lock className="w-5 h-5"/> 3. Veri Güvenliği</h2>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Kimlik bilgileri (T.C., ad, soyad, e-posta, telefon, adres) veritabanında <strong>Fernet (AES-128) ile şifrelenerek</strong> saklanır.</li>
              <li>Parolalar bcrypt (12 round) ile geri döndürülemez şekilde hashlenir.</li>
              <li>Tüm bağlantı SSL/TLS ile şifrelidir (HTTPS).</li>
              <li>Oturum jetonları HttpOnly çerezlerle saklanır, rotasyonel olarak yenilenir.</li>
              <li>Yetkisiz erişim girişimleri sınırlandırılır ve loglanır.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">4. İşleme Amaçları</h2>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Üyelik ve kimlik doğrulama</li>
              <li>Kent konseyi etkinlik/anket katılımı</li>
              <li>Vatandaş taleplerinin yönetimi</li>
              <li>KVKK Madde 5/2-ç uyarınca yasal yükümlülüklerin yerine getirilmesi</li>
              <li>Bilgilendirme e-postaları (yalnızca açık rızanız ile)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">5. Veri Saklama Süresi</h2>
            <p>Aktif üyelik süresi boyunca + hesap silindikten sonra <strong>10 yıl</strong> (kanuni zorunluluk gereği), bu sürenin sonunda kalıcı olarak silinir veya anonimleştirilir.</p>
            <p className="mt-2 text-sm text-slate-600">Erişim kayıtları (giriş/çıkış IP, audit log) güvenlik ve KVKK Madde 12 (denetim) yükümlülüğümüz çerçevesinde <strong>en fazla 2 yıl</strong> tutulur, sonrasında otomatik olarak silinir.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2"><Mail className="w-5 h-5"/> 5a. Üçüncü Taraf Veri Aktarımları</h2>
            <p>Kişisel verileriniz aşağıdaki sınırlı durumlarda, yalnızca belirtilen amaç için yetkili üçüncü taraflara aktarılır:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>E-posta sağlayıcısı (SMTP):</strong> Hesap doğrulama, şifre sıfırlama ve bilgilendirme e-postalarının iletilmesi için ad-soyad ve e-posta adresiniz yapılandırılmış SMTP sunucusuna gönderilir.</li>
              <li><strong>Hukuki yetkili merciler:</strong> Mahkeme/savcılık kararı veya KVKK Madde 28 kapsamında bildirim yükümlülüğü doğduğunda yalnızca talep edilen kayıt iletilir.</li>
              <li><strong>Yurt içi / yurt dışı depolama:</strong> Kişisel verileriniz, Veri Sorumlusu'nun kontrolünde, yurt içinde veya yurt dışında bulunan sunucularda (bulut hizmet sağlayıcıları, sunucu/veri depolama hizmetleri dahil) saklanabilir ve işlenebilir. Yurt dışına aktarım halinde KVKK Madde 9 uyarınca gerekli teknik ve idari güvenlik önlemleri (Fernet/AES-128 şifreleme, sözleşmesel taahhüt vb.) alınır.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">5b. Çerezler</h2>
            <p>Yalnızca <strong>oturum güvenliği</strong> için zorunlu çerezler (HttpOnly, SameSite=Strict) kullanılır. Analitik, reklam veya izleme çerezi <strong>kullanılmaz</strong>. Detaylar için <Link to="/cerez-politikasi" className="text-blue-600 hover:underline">Çerez Politikası</Link> sayfamızı ziyaret edebilirsiniz.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">6. Veri Sahibi Hakları (KVKK Madde 11)</h2>
            <p>Aşağıdaki haklara sahipsiniz:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Verilerinizin işlenip işlenmediğini öğrenme</li>
              <li>İşlenmişse buna ilişkin bilgi talep etme</li>
              <li>Yurt içi/yurt dışında verilerin aktarıldığı üçüncü kişileri bilme</li>
              <li>Eksik/yanlış işlenmişse düzeltilmesini isteme</li>
              <li>Silinmesini veya yok edilmesini isteme</li>
              <li>İletişim izni geçmişinizi görüntüleme</li>
              <li>KVKK'ya şikayette bulunma</li>
            </ul>
            <p className="mt-3">Bu talepleri profil sayfanızdan veya iletişim formundan iletebilirsiniz.</p>
          </section>

          <section className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <h2 className="text-xl font-semibold text-amber-900 flex items-center gap-2 mt-0"><AlertTriangle className="w-5 h-5"/> 7. Veri İhlali Bildirimi</h2>
            <p className="text-amber-900">Verilerinizin yetkisiz kişilerce ele geçirildiğine dair bir şüphe oluşursa, KVKK Madde 12/5 gereği <strong>72 saat içinde</strong> Kişisel Verileri Koruma Kurumu'na ve etkilenen kullanıcılara bildirim yapılır.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2"><Mail className="w-5 h-5"/> 8. İletişim</h2>
            <p>Veri sorumlusuna ulaşmak için <Link to="/iletisim" className="text-blue-600 hover:underline">iletişim sayfamızı</Link> kullanabilir veya KVKK ile ilgili talepleriniz için profil ayarlarınızdaki "KVKK Talepleri" bölümünü ziyaret edebilirsiniz.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
